import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/toast-overrides.css'; // 👈 APRÈS react-toastify pour surpasser les styles
import { secureStorage } from './utils/secureStorage'; // 🔒 NOUVEAU - Stockage sécurisé
import AuthApp from "./components/AuthApp";
import Dashboard from "./components/Dashboard";
import FeatureManager from "./components/FeatureManager";
import BillingPage from "./components/BillingPage";
import SuperAdminDashboard from "./components/SuperAdminDashboard";
import AdminDashboard from "./components/AdminDashboard";
import UserDashboard from "./components/UserDashboard";
import LicenseManager from "./components/LicenseManager";
import CookiePolicy from "./components/CookiePolicy";
import SmartToolsPage from "./components/smartTools/SmartToolsPage"; // 🧠 NOUVEAU - Smart Tools (remplace EmbeddingVisualizer)
import { changeLanguage } from "./i18n/i18n";
import preferencesService from "./services/preferencesService";
import "./styles/dashboard.css";
import "./styles/onboarding.css";
import "./styles/auth.css";
import "./styles/super-admin.css";
import SuperAdminInterface from "./components/SuperAdminDashboardEnhanced";
import TermsAndConditions from "./components/TermsAndConditions";
import CreditsMarketplace from "./components/CreditsMarketplace";
import SEOHead from "./components/SEOHead";

function App() {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false); // Nouveau flag pour éviter les multiples vérifications
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  // Guard to avoid multiple concurrent /auth/profile calls (prevents accidental spamming)
  // This lives at module level per hot-reload lifecycle and avoids triggering server rate-limits
  if (!globalThis.__authProfileRequest) globalThis.__authProfileRequest = { inProgress: false };
  // Vérifier l'authentification au démarrage
  useEffect(() => {
    if (!authChecked) {
      checkAuthStatus();
    }
  }, [authChecked]);

  // Charger les préférences utilisateur après authentification réussie
  useEffect(() => {
    if (isAuthenticated && user) {
      loadUserPreferences();
    }
  }, [isAuthenticated, user]);

  const loadUserPreferences = async () => {
    try {
      console.log('🌍 Chargement des préférences utilisateur...');
      const response = await preferencesService.getPreferences();

      if (response.success && response.preferences) {
        const { language } = response.preferences;

        // Synchroniser la langue avec i18n si différente de la langue actuelle
        if (language) {
          console.log(`🌍 Synchronisation langue: ${language}`);
          changeLanguage(language);
        }
      }
    } catch (error) {
      console.log('⚠️ Impossible de charger les préférences:', error.message);
      // En cas d'erreur, utiliser la langue par défaut ou localStorage
    }
  };

  const checkAuthStatus = async () => {
    if (authChecked) return; // Éviter les appels multiples
    if (globalThis.__authProfileRequest.inProgress) return; // Another check already running
    globalThis.__authProfileRequest.inProgress = true;

    try {
      // 🔒 SÉCURITÉ: Vérifier d'abord le stockage sécurisé
      let storedAuthData = secureStorage.getItem('authData');

      // Migration automatique depuis l'ancien stockage si nécessaire
      if (!storedAuthData) {
        const plainAuthData = localStorage.getItem('authData');
        if (plainAuthData) {
          try {
            storedAuthData = JSON.parse(plainAuthData);
            secureStorage.setItem('authData', storedAuthData); // Migrer
            localStorage.removeItem('authData'); // Nettoyer l'ancien
            console.log('🔄 Migration authData vers stockage sécurisé réussie');
          } catch (error) {
            console.warn('Données locales corrompues, nettoyage...');
            localStorage.removeItem('authData');
          }
        }
      }

      if (storedAuthData) {
        // console.log('📱 Données locales trouvées:', storedAuthData.email); // COMMENTÉ - contient email utilisateur
        setUser(storedAuthData);
        setIsAuthenticated(true);
        setLoading(false);
        setAuthChecked(true);
        return; // Pas besoin de vérifier le serveur si on a des données locales récentes
      }

      console.log('🔍 Vérification auprès du serveur...');
      // Appel d'un endpoint protégé pour vérifier l'auth
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        credentials: 'include' // Inclure les cookies
      });

      if (response.ok) {
        const { user } = await response.json();
        // console.log('✅ Authentification confirmée:', user.email); // COMMENTÉ - contient email utilisateur
        setUser(user);
        setIsAuthenticated(true);
        // 🔒 SÉCURITÉ: Stocker les données utilisateur dans le stockage sécurisé
        secureStorage.setItem('authData', user);
      } else {
        console.log('❌ Authentification échouée');
        // Si échec, nettoyer les données sécurisées
        secureStorage.removeItem('authData');
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.log('❌ Erreur réseau ou non authentifié');
      secureStorage.removeItem('authData');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
      setAuthChecked(true);
      globalThis.__authProfileRequest.inProgress = false;
    }
  };

  // Fonction de déconnexion
  const handleLogout = async () => {
    try {
      // Appeler l'endpoint de déconnexion pour nettoyer le cookie
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Erreur déconnexion:', error);
    } finally {
      // 🔒 SÉCURITÉ: Nettoyer tous les stockages (sécurisé et ancien)
      secureStorage.removeItem('authData');
      localStorage.removeItem('authToken');
      localStorage.removeItem('superAdminToken');
      localStorage.removeItem('authUser');
      localStorage.removeItem('superAdminUser');
      localStorage.removeItem('authData'); 
      setUser(null);
      setIsAuthenticated(false);
      setAuthChecked(true); // Rester à true — résultat = déconnecté, évite re-check immédiat
    }
  };

  // Fonction de connexion réussie
  const handleLoginSuccess = (userData, token) => {
  

    // 🔒 SÉCURITÉ: Stocker seulement les données utilisateur dans le stockage sécurisé
    secureStorage.setItem('authData', userData);

    // Mettre à jour l'état
    setUser(userData);
    setIsAuthenticated(true);
    setAuthChecked(true); // Marquer comme vérifié
  };

  // Écran de chargement
  if (loading) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
        <p>{t('app.loading')}</p>
      </div>
    );
  }

  // Si pas authentifié, afficher AuthApp avec Router pour pages publiques
  if (!isAuthenticated) {
    return (
      <HelmetProvider>
        <Router>
          <Routes>
            <Route
              path="/policy/cookies"
              element={<><SEOHead title="Politique de Cookies" description="Politique de cookies de NodeMented. Découvrez comment nous utilisons les cookies." path="/policy/cookies" /><CookiePolicy /></>}
            />
            <Route
              path="/terms"
              element={<><SEOHead title="Conditions Générales" description="Conditions générales d'utilisation de NodeMented." path="/terms" /><TermsAndConditions /></>}
            />
            <Route
              path="*"
              element={<><SEOHead title="Connexion" description="Connectez-vous à NodeMented - Plateforme intelligente de documentation technique, gestion de tests et spécifications avec IA." path="/" /><AuthApp onLoginSuccess={handleLoginSuccess} user={user} /></>}
            />
          </Routes>
        </Router>
      </HelmetProvider>
    );
  }

  // Si authentifié, router selon le rôle utilisateur
  const renderUserInterface = () => {

    // Super Admin Dashboard
    if (user?.isSuperAdmin && user?.role === 'super-admin') {

      return <SuperAdminInterface user={user} onLogout={handleLogout} />;
    }

    // User Dashboard avec routes existantes - ACCESSIBLE À TOUS LES UTILISATEURS AUTHENTIFIÉS
    return (
      <LicenseManager user={user}>
        <Router>
          <div className="App">
            <SEOHead title="Dashboard" noindex={true} />
            <Routes>
              <Route
                path="/"
                element={<Dashboard user={user} onLogout={handleLogout} />}
              />
              <Route
                path="/admin-dashboard"
                element={<AdminDashboard user={user} onLogout={handleLogout} />}
              />
              <Route
                path="/feature-manager"
                element={<FeatureManager user={user} onLogout={handleLogout} />}
              />
              <Route
                path="/user-dashboard"
                element={<UserDashboard user={user} onLogout={handleLogout} />}
              />
              <Route
                path="/billing"
                element={<BillingPage user={user} onBackToDashboard={() => window.location.href = '/'} />}
              />
              <Route
                path="/marketplace"
                element={<CreditsMarketplace user={user} onBackToDashboard={() => window.location.href = '/'} />}
              />
              <Route
                path="/policy/cookies"
                element={<CookiePolicy />}
              />
              <Route
                path="/terms"
                element={<TermsAndConditions />}
              />
              <Route
                path="/smart-tools"
                element={<SmartToolsPage />}
              />
            </Routes>
          </div>
        </Router>
      </LicenseManager>
    );
  };

  return (
    <HelmetProvider>
      {/* 🎯 Configuration globale des toasts - AU NIVEAU RACINE */}
      <ToastContainer
        position="top-right"
        autoClose={10000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        zIndex={999999}
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Le reste de l'app */}
      {renderUserInterface()}
    </HelmetProvider>
  );
}

export default App;
